import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import SpectraToolbar, { ANALYSIS_CATEGORIES } from './spectra/SpectraToolbar';
import SpectraNavigator, { DataTableEntry, AnalysisEntry, GraphEntry } from './spectra/SpectraNavigator';
import SpectraDataGrid from './spectra/SpectraDataGrid';
import SpectraGraph, {
  type GraphConfig,
  buildGroupComparisonGraph,
  buildCorrelationGraph,
  buildDescriptiveGraph,
} from './spectra/SpectraGraph';
import SpectraResults from './spectra/SpectraResults';
import { type ParsedData, extractGroups, extractXY } from './spectra/spectraUtils';
import { EXAMPLE_DATASETS, getExamplesForCategory } from './spectra/spectraExamples';

let nextTableId = 1;
let nextAnalysisId = 1;

const SpectraWorkspace: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Core state
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeSubOption, setActiveSubOption] = useState<string | null>(null);
  const [tables, setTables] = useState<DataTableEntry[]>([{ id: 'table_1', name: 'Untitled' }]);
  const [activeTableId, setActiveTableId] = useState<string>('table_1');
  const [tableData, setTableData] = useState<Record<string, ParsedData | null>>({});
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [graphConfig, setGraphConfig] = useState<GraphConfig | null>(null);
  const [analyses, setAnalyses] = useState<AnalysisEntry[]>([]);
  const [graphs, setGraphs] = useState<GraphEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse URL params on mount
  useEffect(() => {
    const mode = searchParams.get('mode');
    const sub = searchParams.get('sub');
    if (mode) {
      setActiveCategory(mode);
      if (sub) setActiveSubOption(sub);
    }
  }, [searchParams]);

  const currentData = tableData[activeTableId] || null;

  const handleSelectCategory = useCallback((id: string) => {
    setActiveCategory(id);
    const cat = ANALYSIS_CATEGORIES.find(c => c.id === id);
    if (cat && cat.subOptions.length > 0) {
      setActiveSubOption(cat.subOptions[0].id);
    }
  }, []);

  const handleSelectSubOption = useCallback((id: string) => {
    setActiveSubOption(id);
  }, []);

  const handleDataChange = useCallback((data: ParsedData) => {
    setTableData(prev => ({ ...prev, [activeTableId]: data }));
  }, [activeTableId]);

  const handleAddTable = useCallback(() => {
    nextTableId++;
    const id = `table_${nextTableId}`;
    setTables(prev => [...prev, { id, name: `Table ${nextTableId}` }]);
    setActiveTableId(id);
  }, []);

  const handleRenameTable = useCallback((id: string, name: string) => {
    setTables(prev => prev.map(t => t.id === id ? { ...t, name } : t));
  }, []);

  const handleLoadExample = useCallback(() => {
    const examples = activeCategory ? getExamplesForCategory(activeCategory) : EXAMPLE_DATASETS;
    if (examples.length === 0) return;
    const ex = examples[0];
    const parsed: ParsedData = {
      headers: ex.headers,
      rows: ex.rows,
      detectedType: ex.headers.length === 2 && typeof ex.rows[0][0] === 'number' && typeof ex.rows[0][1] === 'number' ? 'xy' : 'column',
      columnTypes: ex.headers.map((_, ci) => {
        const numCount = ex.rows.filter(r => typeof r[ci] === 'number').length;
        return numCount / ex.rows.length > 0.8 ? 'numeric' : 'text';
      }) as any,
    };
    handleDataChange(parsed);
    // Auto-rename table
    handleRenameTable(activeTableId, ex.name);
  }, [activeCategory, activeTableId, handleDataChange, handleRenameTable]);

  const handleRunAnalysis = useCallback(async () => {
    if (!currentData || !activeSubOption) return;

    setLoading(true);
    setError(null);

    try {
      // Build request payload based on test type
      const groups = extractGroups(currentData);
      const xy = extractXY(currentData);

      let payload: any = { test: activeSubOption, alpha: 0.05 };

      // Determine which data shape to send
      const twoGroupTests = ['unpaired_t', 'paired_t', 'welch_t', 'mann_whitney'];
      const multiGroupTests = ['one_way_anova', 'kruskal_wallis'];
      const correlationTests = ['pearson', 'spearman', 'linear_regression'];

      if (twoGroupTests.includes(activeSubOption)) {
        if (groups.length < 2) {
          setError('Need at least 2 numeric columns for this test');
          setLoading(false);
          return;
        }
        payload.groups = groups.slice(0, 2);
      } else if (multiGroupTests.includes(activeSubOption)) {
        if (groups.length < 2) {
          setError('Need at least 2 numeric columns for this test');
          setLoading(false);
          return;
        }
        payload.groups = groups;
        payload.post_hoc = activeSubOption === 'one_way_anova' ? 'tukey' : 'dunn';
      } else if (correlationTests.includes(activeSubOption)) {
        if (!xy) {
          setError('Need two numeric columns (X and Y) for this test');
          setLoading(false);
          return;
        }
        payload.x = xy.x;
        payload.y = xy.y;
      } else if (activeSubOption === 'descriptive') {
        if (groups.length < 1) {
          setError('Need at least one numeric column');
          setLoading(false);
          return;
        }
        payload.groups = [groups[0]];
      } else {
        // For curve fitting / SPR — not yet wired to backend, show placeholder
        setError(`${activeSubOption} analysis will be available in Phase 2. Use Compare Groups, Correlation, or Descriptive.`);
        setLoading(false);
        return;
      }

      const resp = await api.post('/v2/spectra/statistics', payload);
      const result = resp.data;
      setAnalysisResult(result);

      // Build graph
      let graph: GraphConfig | null = null;
      if (twoGroupTests.includes(activeSubOption) || multiGroupTests.includes(activeSubOption)) {
        graph = buildGroupComparisonGraph(result, currentData.headers, groups, activeSubOption);
      } else if (correlationTests.includes(activeSubOption) && xy) {
        graph = buildCorrelationGraph(result, xy.x, xy.y, currentData.headers);
      } else if (activeSubOption === 'descriptive') {
        graph = buildDescriptiveGraph(result, groups[0]);
      }
      setGraphConfig(graph);

      // Record in navigator
      nextAnalysisId++;
      const aId = `analysis_${nextAnalysisId}`;
      setAnalyses(prev => [...prev, {
        id: aId,
        label: result.test_name,
        type: activeSubOption,
        timestamp: Date.now(),
      }]);
      if (graph) {
        setGraphs(prev => [...prev, {
          id: `graph_${nextAnalysisId}`,
          label: result.test_name,
          type: graph!.type,
        }]);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Analysis failed';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [currentData, activeSubOption]);

  // Can run analysis?
  const canRun = !!currentData && currentData.rows.length > 0 && !!activeSubOption;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--main-bg)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderBottom: '1px solid var(--card-border)',
        background: 'var(--card-bg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            fontSize: '18px',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Spectra
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            Statistical Analysis Workspace
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <button
            onClick={handleLoadExample}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--card-border)',
              background: 'var(--hover-bg)',
              color: 'var(--text-secondary)',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Load Example
          </button>
          <button
            onClick={handleRunAnalysis}
            disabled={!canRun || loading}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: 'none',
              background: canRun && !loading
                ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                : 'var(--card-border)',
              color: canRun && !loading ? '#fff' : 'var(--text-secondary)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: canRun && !loading ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.15s ease',
            }}
          >
            {loading ? 'Running...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <SpectraToolbar
        activeCategory={activeCategory}
        activeSubOption={activeSubOption}
        onSelectCategory={handleSelectCategory}
        onSelectSubOption={handleSelectSubOption}
      />

      {/* Error banner */}
      {error && (
        <div style={{
          padding: '8px 16px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#ef4444',
          fontSize: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}
          >
            \u00D7
          </button>
        </div>
      )}

      {/* Main 3-panel layout */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Left navigator */}
        <SpectraNavigator
          tables={tables}
          activeTableId={activeTableId}
          analyses={analyses}
          graphs={graphs}
          onSelectTable={setActiveTableId}
          onAddTable={handleAddTable}
          onRenameTable={handleRenameTable}
          onSelectAnalysis={() => {}}
          onSelectGraph={() => {}}
        />

        {/* Centre: Grid + Graph */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          {/* Data grid (top portion) */}
          <div style={{ flex: graphConfig ? '0 0 45%' : 1, minHeight: '150px', display: 'flex', flexDirection: 'column', borderBottom: graphConfig ? '1px solid var(--card-border)' : 'none' }}>
            <SpectraDataGrid data={currentData} onDataChange={handleDataChange} />
          </div>

          {/* Graph (bottom portion) */}
          {graphConfig && (
            <div style={{ flex: '1 1 55%', minHeight: '200px' }}>
              <SpectraGraph config={graphConfig} />
            </div>
          )}
        </div>

        {/* Right results panel */}
        <SpectraResults
          result={analysisResult}
          activeCategory={activeCategory}
          activeSubOption={activeSubOption}
        />
      </div>
    </div>
  );
};

export default SpectraWorkspace;
